import { useEffect, useState } from 'react';
import api from '../utils/api';
import './Messages.css';

const Messages = () => {
    interface Message {
        id?: number;
        message_id?: number;
        first_name?: string;
        lastName?: string;
        last_name?: string;
        firstName?: string;
        email?: string;
        phone_number?: string;
        phone?: string;
        message?: string;
        body?: string;
    }

    const [messages, setMessages] = useState<Message[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedMessage, setSelectedMessage] = useState<Message | null>(null);
    const [showMessageModal, setShowMessageModal] = useState(false);
    const [notifications, setNotifications] = useState<{ id: number; message: string; type: 'success' | 'error' | 'info' }[]>([]);

    const showNotification = (message: string, type: 'success' | 'error' | 'info' = 'info') => {
        const id = Date.now();
        setNotifications(prev => [...(Array.isArray(prev) ? prev : []), { id, message, type }]);
        setTimeout(() => {
            setNotifications(prev => (Array.isArray(prev) ? prev : []).filter(n => n.id !== id));
        }, 5000);
    };

    // Fetch messages from the backend
    useEffect(() => {
        const fetchMessages = async () => {
            try {
                const response = await api.get('/v1/vitrine/contact/messages');
                const data = response.data;
                const raw = Array.isArray(data) ? data : (data && typeof data === 'object' ? (data.messages ?? data.data ?? data.items) : null);
                const list = Array.isArray(raw) ? raw : [];
                setMessages(list);
            } catch (err: any) {
                showNotification(err.response?.data?.message || 'Failed to fetch messages', 'error');
            } finally {
                setIsLoading(false);
            }
        };

        fetchMessages();
    }, []);

    if (isLoading) return <div className="messages-container"><div className="loader">Loading messages...</div></div>;

    return (
        <div className='messages-container'>
            <div className="notification-tray">
                {(Array.isArray(notifications) ? notifications : []).map((n) => (
                    <div key={n.id} className={`notification ${n.type}`}>
                        {n.type === 'success' ? '✅' : n.type === 'error' ? '❌' : 'ℹ️'} {n.message}
                    </div>
                ))}
            </div>

            <div className="messages-header">
                <h2>User Inquiries</h2>
            </div>

            <div className="table-wrapper">
                <table className="modern-table">
                    <thead>
                        <tr>
                            <th>Sender</th>
                            <th>Email Address</th>
                            <th>Phone</th>
                            <th>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {(Array.isArray(messages) ? messages : []).map((msg, index) => (
                            <tr key={msg?.id ?? msg?.message_id ?? index}>
                                <td>
                                    <strong>{msg?.first_name ?? msg?.firstName ?? ''} {msg?.last_name ?? msg?.lastName ?? ''}</strong>
                                </td>
                                <td>
                                    <a href={`mailto:${msg?.email ?? ''}`} className="contact-email">
                                        {msg?.email ?? ''}
                                    </a>
                                </td>
                                <td>
                                    <span className="contact-phone">{msg?.phone_number ?? msg?.phone ?? ''}</span>
                                </td>
                                <td>
                                    <button
                                        className="btn-icon btn-view"
                                        onClick={() => {
                                            setSelectedMessage(msg ?? null);
                                            setShowMessageModal(true);
                                        }}
                                    >
                                        Read Message
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Message Content Modal */}
            {showMessageModal && selectedMessage && (
                <div className="modal-overlay">
                    <div className="glass-modal">
                        <div className="modal-header">
                            <h2 style={{ fontSize: '1.8rem', fontWeight: 700 }}>Inquiry Details</h2>
                            <p style={{ opacity: 0.6 }}>Reference ID: #{selectedMessage?.id ?? selectedMessage?.message_id ?? '—'}</p>
                        </div>

                        <div className="modal-sender-info">
                            <div>
                                <small style={{ display: 'block', opacity: 0.6 }}>FROM</small>
                                <strong>{selectedMessage?.first_name ?? selectedMessage?.firstName ?? ''} {selectedMessage?.last_name ?? selectedMessage?.lastName ?? ''}</strong>
                            </div>
                            <div>
                                <small style={{ display: 'block', opacity: 0.6 }}>CONTACT</small>
                                <span>{selectedMessage?.email ?? ''}</span>
                            </div>
                        </div>

                        <div className="message-text">
                            {selectedMessage?.message ?? selectedMessage?.body ?? ''}
                        </div>

                        <div className="modal-footer">
                            <button
                                className="btn-close"
                                onClick={() => setShowMessageModal(false)}
                            >
                                Close Details
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Messages;